import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card, Table, Button, Form, Row, Col, Badge, Modal,
  Spinner, InputGroup, Pagination, Alert, Toast, ToastContainer,
  Accordion
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/api';

const BILLING_GROUPS = [
  'Prosedur Non Bedah',
  'Prosedur Bedah',
  'Konsultasi',
  'Tenaga Ahli',
  'Keperawatan',
  'Penunjang',
  'Radiologi',
  'Laboratorium',
  'Pelayanan Darah',
  'Rehabilitasi',
  'Kamar/Akomodasi',
  'Rawat Intensif',
  'Obat',
  'Obat Kronis',
  'Obat Kemoterapi',
  'Alkes',
  'BMHP',
  'Sewa Alat'
];

const GROUP_COLORS = {
  'Prosedur Non Bedah': { bg: '#e0f2fe', text: '#0369a1', border: '#7dd3fc' },
  'Prosedur Bedah':     { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
  'Konsultasi':         { bg: '#fef3c7', text: '#b45309', border: '#fde68a' },
  'Tenaga Ahli':        { bg: '#ffedd5', text: '#c2410c', border: '#fed7aa' },
  'Keperawatan':        { bg: '#f3e8ff', text: '#7e22ce', border: '#d8b4fe' },
  'Penunjang':          { bg: '#e0e7ff', text: '#4338ca', border: '#c7d2fe' },
  'Radiologi':          { bg: '#ccfbf1', text: '#0f766e', border: '#99f6e4' },
  'Laboratorium':       { bg: '#dcfce7', text: '#15803d', border: '#86efac' },
  'Pelayanan Darah':    { bg: '#ffe4e6', text: '#be123c', border: '#fecdd3' },
  'Rehabilitasi':       { bg: '#ecfdf5', text: '#047857', border: '#a7f3d0' },
  'Kamar/Akomodasi':    { bg: '#f1f5f9', text: '#334155', border: '#cbd5e1' },
  'Rawat Intensif':     { bg: '#fae8ff', text: '#86198f', border: '#f0abfc' },
  'Obat':               { bg: '#fef9c3', text: '#854d0e', border: '#fef08a' },
  'Obat Kronis':        { bg: '#fef08a', text: '#713f12', border: '#fde047' },
  'Obat Kemoterapi':    { bg: '#fbcfe8', text: '#9d174d', border: '#f472b6' },
  'Alkes':              { bg: '#cffafe', text: '#0e7490', border: '#67e8f9' },
  'BMHP':               { bg: '#e2e8f0', text: '#475569', border: '#94a3b8' },
  'Sewa Alat':          { bg: '#ede9fe', text: '#6d28d9', border: '#c4b5fd' }
};

const DEFINISI_OPERASIONAL = [
  { no: 1, group: 'Prosedur Non Bedah', def: 'Tindakan medis diagnostik atau terapeutik non-invasif/tanpa pembedahan (seperti EKG, nebulizer, kateterisasi, USG tindakan, pasang NGT, wound dressing sederhana).' },
  { no: 2, group: 'Prosedur Bedah', def: 'Tindakan operatif di kamar operasi (OK) mencakup bedah mayor, moderat, minor, anestesi, laparoskopi, section caesarea, curretage, orif/reposisi bedah.' },
  { no: 3, group: 'Konsultasi', def: 'Jasa pemeriksaan dan konsultasi klinis oleh dokter penanggung jawab pelayanan (DPJP), dokter spesialis, konsulan, atau dokter umum (visite rawat inap, poli rawat jalan).' },
  { no: 4, group: 'Tenaga Ahli', def: 'Jasa pelayanan profesional tenaga kesehatan terlatih non-dokter seperti fisioterapis, dietisien/ahli gizi, psikolog klinis, perawat anestesi.' },
  { no: 5, group: 'Keperawatan', def: 'Biaya asuhan keperawatan dan kebidanan rutin harian pasien selama masa perawatan di bangsal rawat inap atau observasi.' },
  { no: 6, group: 'Penunjang', def: 'Pemeriksaan penunjang diagnostik selain lab & radiologi, seperti audiometri, spirometri, endoskopi, kolonoskopi, hemodialisa reguler.' },
  { no: 7, group: 'Radiologi', def: 'Pemeriksaan pencitraan diagnostik meliputi rontgen/X-Ray foto thorax, abdomen, CT-Scan, USG penunjang, MRI, Panoramic dental, Mammografi.' },
  { no: 8, group: 'Laboratorium', def: 'Pemeriksaan patologi klinik, patologi anatomi, mikrobiologi, kimia darah, hematologi lengkap, urinalisis, feses, imunologi-serologi, analisa gas darah.' },
  { no: 9, group: 'Pelayanan Darah', def: 'Penyediaan kantong darah transfusi (Whole Blood, PRC, FFP, TC, Cryoprecipitate), uji silang serasi (crossmatch), dan tindakan transfusi darah.' },
  { no: 10, group: 'Rehabilitasi', def: 'Layanan terapi medis pemulihan fisik dan fungsional oleh instalasi rehabilitasi medik (fisioterapi, terapi wicara, terapi okupasi).' },
  { no: 11, group: 'Kamar/Akomodasi', def: 'Biaya sewa tempat tidur dan fasilitas ruang rawat inap kelas umum (Kelas 1, Kelas 2, Kelas 3, VIP, Isolasi standar).' },
  { no: 12, group: 'Rawat Intensif', def: 'Biaya ruang rawat intensif khusus dengan monitoring ketat hemodinamik (ICU, ICCU, PICU, NICU, HCU).' },
  { no: 13, group: 'Obat', def: 'Biaya obat-obatan terapi rutin, antibiotik, analgetik, infus cairan dasar yang diberikan selama perawatan rawat jalan atau rawat inap.' },
  { no: 14, group: 'Obat Kronis', def: 'Obat program rujuk balik (PRB) dan terapi penyakit kronis 30 hari (hipertensi, diabetes, PPOK, asma, jantung stabil).' },
  { no: 15, group: 'Obat Kemoterapi', def: 'Obat-obatan sitostatika dan terapi target khusus untuk protokol tatalaksana kanker dan neoplasma.' },
  { no: 16, group: 'Alkes', def: 'Alat kesehatan medis tahan pakai atau implant (seperti kateter double lumen, plat/screw ortopedi, mesh hernia, lensa intraokular).' },
  { no: 17, group: 'BMHP', def: 'Bahan medis habis pakai sekali pakai seperti spuit injeksi, kasa steril, perban, sarung tangan steril, plester, desinfektan, infus set.' },
  { no: 18, group: 'Sewa Alat', def: 'Biaya penggunaan alat medik canggih per sesi/hari seperti ventilator mekanik, syringe pump, infus pump, CPAP/BiPAP, phototherapy infant.' }
];

export default function BillingGroupEklaim() {
  const navigate = useNavigate();

  // Data & stats state
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [groupCounts, setGroupCounts] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [modifiedRows, setModifiedRows] = useState({});

  // Filter & Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroupFilter, setSelectedGroupFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Time header
  const [clockTime, setClockTime] = useState({ date: '', clock: '' });

  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Toast state
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const showNotification = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
  };

  // Clock effect
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setClockTime({
        date: now.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' }),
        clock: now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      });
    };
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch list
  const fetchList = useCallback(async (page = currentPage, group = selectedGroupFilter, query = searchQuery) => {
    setIsLoading(true);
    try {
      const apiUrl = getApiUrl();
      const params = new URLSearchParams({
        search: query,
        billing_group: group === 'ALL' ? '' : group,
        page: page.toString(),
        per_page: itemsPerPage.toString()
      });

      const res = await fetch(`${apiUrl}/api/billing_group/list?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();

      if (json.status === 1 || json.status === 'success') {
        setItems(json.data || []);
        setTotalCount(json.filtered_total !== undefined ? json.filtered_total : (json.total || 0));
        setGroupCounts(json.summary_counts || json.group_counts || {});
      } else {
        showNotification(json.msg || json.message || 'Gagal memuat data billing group', 'danger');
      }
    } catch (err) {
      console.error('Error fetching billing groups:', err);
      showNotification('Gagal menghubungi backend SIMRS: ' + err.message, 'danger');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, selectedGroupFilter, searchQuery]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Handle single row change
  const handleRowChange = (kd_tindakan, field, value) => {
    setItems(prev => prev.map(item => {
      if (item.kd_tindakan === kd_tindakan) {
        return { ...item, [field]: value };
      }
      return item;
    }));

    setModifiedRows(prev => ({
      ...prev,
      [kd_tindakan]: {
        ...(prev[kd_tindakan] || items.find(i => i.kd_tindakan === kd_tindakan) || {}),
        [field]: value,
        kd_tindakan
      }
    }));
  };

  // Save single row
  const handleSaveSingle = async (item) => {
    setSavingKey(item.kd_tindakan);
    try {
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/api/billing_group/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kd_tindakan: item.kd_tindakan,
          billing_group: item.billing_group,
          kode_kptl: item.kode_kptl || ''
        })
      });
      const json = await res.json();
      if (res.ok && (json.status === 1 || json.status === 'success')) {
        showNotification(json.msg || `Layanan ${item.kd_tindakan} (${item.nm_tindakan}) berhasil disimpan!`, 'success');
        setModifiedRows(prev => {
          const next = { ...prev };
          delete next[item.kd_tindakan];
          return next;
        });
        // update local source
        setItems(prev => prev.map(i => i.kd_tindakan === item.kd_tindakan ? { ...i, source: 'MANUAL' } : i));
      } else {
        showNotification(json.msg || json.message || 'Gagal menyimpan', 'danger');
      }
    } catch (err) {
      showNotification('Error saat menyimpan: ' + err.message, 'danger');
    } finally {
      setSavingKey(null);
    }
  };

  // Save all modified rows
  const handleSaveAllModified = async () => {
    const modifiedList = Object.values(modifiedRows);
    if (modifiedList.length === 0) {
      showNotification('Tidak ada perubahan baris yang perlu disimpan.', 'info');
      return;
    }

    setIsBulkSaving(true);
    try {
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/api/billing_group/bulk_save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: modifiedList })
      });
      const json = await res.json();
      if (res.ok && (json.status === 1 || json.status === 'success')) {
        showNotification(json.msg || json.message || `Berhasil memperbarui ${json.count} layanan!`, 'success');
        setModifiedRows({});
        fetchList(currentPage, selectedGroupFilter, searchQuery);
      } else {
        showNotification(json.msg || json.message || 'Gagal simpan massal', 'danger');
      }
    } catch (err) {
      showNotification('Error simpan massal: ' + err.message, 'danger');
    } finally {
      setIsBulkSaving(false);
    }
  };

  // Download Excel
  const handleDownloadExcel = () => {
    const apiUrl = getApiUrl();
    window.open(`${apiUrl}/api/billing_group/download_excel`, '_blank');
  };

  // Download CSV
  const handleDownloadCsv = () => {
    const apiUrl = getApiUrl();
    window.open(`${apiUrl}/api/billing_group/download_csv`, '_blank');
  };

  // Upload Excel / CSV
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      showNotification('Pilih file template (.xlsx / .csv) terlebih dahulu!', 'warning');
      return;
    }

    setIsUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/api/billing_group/upload_excel`, {
        method: 'POST',
        body: formData
      });
      const json = await res.json();
      if (res.ok && (json.status === 1 || json.status === 'success')) {
        setUploadResult({
          type: 'success',
          message: json.msg || json.message || `Berhasil mengimpor ${json.count} layanan!`,
          skipped: json.skipped || []
        });
        showNotification(`Berhasil impor ${json.count} layanan!`, 'success');
        fetchList(1, 'ALL', '');
      } else {
        setUploadResult({
          type: 'danger',
          message: json.msg || json.message || 'Gagal mengimpor file template.'
        });
      }
    } catch (err) {
      setUploadResult({
        type: 'danger',
        message: 'Gagal upload: ' + err.message
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Reset Auto Heuristics
  const handleResetAuto = async () => {
    setIsResetting(true);
    try {
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/api/billing_group/reset_auto`, {
        method: 'POST'
      });
      const json = await res.json();
      if (res.ok && (json.status === 1 || json.status === 'success')) {
        showNotification(json.msg || json.message || `Berhasil reset ${json.count} tarif otomatis!`, 'success');
        setShowResetConfirmModal(false);
        setModifiedRows({});
        fetchList(1, 'ALL', '');
      } else {
        showNotification(json.msg || json.message || 'Gagal mereset otomatis', 'danger');
      }
    } catch (err) {
      showNotification('Error reset: ' + err.message, 'danger');
    } finally {
      setIsResetting(false);
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
  const modifiedCount = Object.keys(modifiedRows).length;

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      
      {/* ══ TOAST NOTIFICATION ══ */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          show={toast.show}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
          delay={4000}
          autohide
          bg={toast.variant}
        >
          <Toast.Header closeButton>
            <strong className="me-auto text-white">Notifikasi E-Klaim</strong>
          </Toast.Header>
          <Toast.Body className="text-white fw-medium">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* ══ TOP NAVBAR ══ */}
      <nav style={{ background: 'linear-gradient(90deg, #0d9488 0%, #0f766e 100%)', color: 'white', padding: '0.65rem 1.25rem' }} className="d-flex align-items-center justify-content-between shadow-sm sticky-top">
        <div className="d-flex align-items-center gap-3">
          <div style={{ background: '#ffffff22', borderRadius: '8px', padding: '0.4rem 0.55rem', display: 'flex', alignItems: 'center' }}>
            <i className="bi bi-file-earmark-spreadsheet-fill text-warning fs-5"></i>
          </div>
          <div>
            <div className="fw-bold fs-6 lh-1">SIMRS — Master Mapping Template Billing Group E-Klaim</div>
            <div className="text-light" style={{ fontSize: '0.72rem', opacity: 0.9 }}>
              Format Baku 18 Kategori Tarif Kemenkes RI & Integrasi Otomatis group_tarif()
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span className="text-light d-none d-md-inline" style={{ fontSize: '0.75rem', opacity: 0.95 }}>
            <i className="bi bi-calendar3 me-1"></i>{clockTime.date} | <i className="bi bi-clock me-1"></i>{clockTime.clock}
          </span>
          <Button
            variant="warning"
            size="sm"
            className="fw-bold text-dark shadow-sm d-flex align-items-center gap-1"
            style={{ fontSize: '0.74rem', padding: '0.28rem 0.75rem', borderRadius: '6px' }}
            onClick={() => navigate('/DataClaimPasien')}
          >
            <i className="bi bi-arrow-left"></i> Ke Data Klaim Pasien
          </Button>
          <Button
            variant="outline-light"
            size="sm"
            className="fw-bold shadow-sm d-flex align-items-center gap-1"
            style={{ fontSize: '0.74rem', padding: '0.28rem 0.75rem', borderRadius: '6px' }}
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-house-door-fill"></i> Home
          </Button>
        </div>
      </nav>

      <div className="p-3" style={{ maxWidth: '1600px', margin: '0 auto' }}>

        {/* ══ STATS SUMMARY CARDS ══ */}
        <Row className="g-2 mb-3">
          <Col lg={3} sm={6}>
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '0.75rem 1rem' }} className="shadow-sm d-flex align-items-center gap-3">
              <div style={{ background: '#0d9488', color: 'white', borderRadius: '8px', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                <i className="bi bi-list-check"></i>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>Total Layanan Tindakan</div>
                <div className="fw-bold text-dark" style={{ fontSize: '1.25rem', lineHeight: 1 }}>{totalCount.toLocaleString('id-ID')}</div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>Sinkron dari mst_tindakan</div>
              </div>
            </div>
          </Col>

          <Col lg={3} sm={6}>
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '0.75rem 1rem' }} className="shadow-sm d-flex align-items-center gap-3">
              <div style={{ background: '#0284c7', color: 'white', borderRadius: '8px', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                <i className="bi bi-diagram-3-fill"></i>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>18 Kategori E-Klaim</div>
                <div className="fw-bold text-primary" style={{ fontSize: '1.25rem', lineHeight: 1 }}>18 / 18</div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>Standar Resmi Kemenkes RI</div>
              </div>
            </div>
          </Col>

          <Col lg={3} sm={6}>
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '0.75rem 1rem' }} className="shadow-sm d-flex align-items-center gap-3">
              <div style={{ background: '#16a34a', color: 'white', borderRadius: '8px', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                <i className="bi bi-file-earmark-check-fill"></i>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>Ter-mapping Otomatis</div>
                <div className="fw-bold text-success" style={{ fontSize: '1.25rem', lineHeight: 1 }}>
                  {totalCount.toLocaleString('id-ID')}
                </div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>Heuristik group_tarif() aktif</div>
              </div>
            </div>
          </Col>

          <Col lg={3} sm={6}>
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '0.75rem 1rem' }} className="shadow-sm d-flex align-items-center gap-3">
              <div style={{ background: modifiedCount > 0 ? '#ea580c' : '#64748b', color: 'white', borderRadius: '8px', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                <i className="bi bi-pencil-square"></i>
              </div>
              <div className="flex-grow-1">
                <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>Perubahan Belum Disimpan</div>
                <div className="fw-bold" style={{ fontSize: '1.25rem', lineHeight: 1, color: modifiedCount > 0 ? '#ea580c' : '#64748b' }}>
                  {modifiedCount} <span style={{ fontSize: '0.75rem', fontWeight: 400 }}>baris</span>
                </div>
                {modifiedCount > 0 ? (
                  <Button
                    variant="warning"
                    size="sm"
                    className="fw-bold py-0 px-2 mt-1 shadow-sm"
                    style={{ fontSize: '0.68rem' }}
                    onClick={handleSaveAllModified}
                    disabled={isBulkSaving}
                  >
                    {isBulkSaving ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-save me-1"></i>Simpan {modifiedCount} Baris</>}
                  </Button>
                ) : (
                  <div className="text-muted" style={{ fontSize: '0.68rem' }}>Semua data tersinkron</div>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* ══ DEFINISI OPERASIONAL ACCORDION ══ */}
        <Accordion className="mb-3 shadow-sm border-0" defaultActiveKey={null}>
          <Accordion.Item eventKey="0" style={{ border: '1px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden' }}>
            <Accordion.Header>
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-info-circle-fill text-teal" style={{ color: '#0d9488' }}></i>
                <span className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
                  Petunjuk & Definisi Operasional 18 Billing Group E-Klaim (Kemenkes)
                </span>
                <Badge bg="light" text="dark" className="border ms-2" style={{ fontSize: '0.68rem' }}>
                  Format Excel Resmi Row 1 & 2
                </Badge>
              </div>
            </Accordion.Header>
            <Accordion.Body style={{ background: '#f8fafc', fontSize: '0.78rem' }}>
              <Row className="g-3 mb-2">
                <Col md={6}>
                  <div className="p-2 bg-white rounded border">
                    <div className="fw-bold text-teal mb-1" style={{ color: '#0f766e' }}>
                      <i className="bi bi-file-earmark-spreadsheet me-1"></i> Struktur File Excel Template Kemenkes:
                    </div>
                    <ul className="mb-1 ps-3 text-muted">
                      <li><strong>Baris 1:</strong> Berisi penomoran kolom <code>1, 2, 3, 4</code></li>
                      <li><strong>Baris 2:</strong> Berisi header kolom baku: <code>kode layanan, nama layanan, billing group e-klaim, kode kptl (opsional)</code></li>
                      <li><strong>Baris 3+:</strong> Data tindakan rumah sakit (1.525 baris) yang telah diklasifikasikan</li>
                      <li><strong>Sheet 2:</strong> <code>Definisi Operasional</code> (Berisi acuan 18 grup dan validasi dropdown otomatis)</li>
                    </ul>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="p-2 bg-white rounded border">
                    <div className="fw-bold text-primary mb-1">
                      <i className="bi bi-gear-wide-connected me-1"></i> Integrasi Otomatis dengan Bridging E-Klaim:
                    </div>
                    <p className="mb-0 text-muted">
                      Setiap kali proses bridging dan grouping E-Klaim (INACBG/iDRG) berjalan di menu <strong>Data Klaim Pasien</strong>, sistem SIMRS secara langsung menggunakan tabel mapping ini untuk membagi rincian biaya tagihan pasien (piutang tindakan) ke dalam 18 grup tarif tanpa perlu manual koding berulang.
                    </p>
                  </div>
                </Col>
              </Row>

              <div className="table-responsive bg-white rounded border p-2" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                <Table size="sm" bordered hover className="align-middle mb-0" style={{ fontSize: '0.74rem' }}>
                  <thead className="table-light sticky-top">
                    <tr>
                      <th style={{ width: '40px', textAlign: 'center' }}>No</th>
                      <th style={{ width: '180px' }}>Billing Group</th>
                      <th>Definisi Operasional / Ruang Lingkup Tindakan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEFINISI_OPERASIONAL.map(d => {
                      const color = GROUP_COLORS[d.group] || { bg: '#f1f5f9', text: '#334155' };
                      return (
                        <tr key={d.no}>
                          <td className="text-center fw-semibold text-muted">{d.no}</td>
                          <td>
                            <Badge style={{ background: color.bg, color: color.text, border: `1px solid ${color.border || '#cbd5e1'}`, padding: '0.3rem 0.5rem', fontSize: '0.73rem' }}>
                              {d.group}
                            </Badge>
                          </td>
                          <td className="text-secondary">{d.def}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        {/* ══ MAIN TABLE CARD ══ */}
        <Card className="shadow-sm border-0" style={{ borderRadius: '10px' }}>
          
          {/* Card Top Action Toolbar */}
          <div className="p-3 border-bottom d-flex flex-wrap gap-2 align-items-center justify-content-between bg-white" style={{ borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}>
            
            {/* Left Action Buttons */}
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <Button
                variant="success"
                size="sm"
                className="fw-bold d-flex align-items-center shadow-sm text-white"
                style={{ background: '#059669', borderColor: '#059669', fontSize: '0.75rem', padding: '0.35rem 0.75rem' }}
                onClick={handleDownloadExcel}
                title="Download template Excel resmi Kemenkes lengkap dengan Sheet Definisi Operasional dan Dropdown otomatis"
              >
                <i className="bi bi-file-earmark-excel-fill me-1"></i> Download Template Excel (.xlsx)
              </Button>

              <Button
                variant="outline-secondary"
                size="sm"
                className="fw-bold d-flex align-items-center shadow-sm"
                style={{ fontSize: '0.75rem', padding: '0.35rem 0.75rem' }}
                onClick={handleDownloadCsv}
                title="Download data template dalam format CSV"
              >
                <i className="bi bi-filetype-csv me-1"></i> Download CSV
              </Button>

              <Button
                variant="primary"
                size="sm"
                className="fw-bold d-flex align-items-center shadow-sm"
                style={{ fontSize: '0.75rem', padding: '0.35rem 0.75rem', background: '#0284c7', borderColor: '#0284c7' }}
                onClick={() => {
                  setUploadFile(null);
                  setUploadResult(null);
                  setShowUploadModal(true);
                }}
                title="Unggah template Excel atau CSV yang telah diisi / diedit untuk update massal"
              >
                <i className="bi bi-cloud-arrow-up-fill me-1"></i> Upload File Template
              </Button>

              <Button
                variant="outline-warning"
                size="sm"
                className="fw-bold d-flex align-items-center shadow-sm text-dark"
                style={{ fontSize: '0.75rem', padding: '0.35rem 0.75rem' }}
                onClick={() => setShowResetConfirmModal(true)}
                title="Sinkronisasi ulang semua tindakan dengan algoritma klasifikasi otomatis group_tarif()"
              >
                <i className="bi bi-magic me-1 text-warning"></i> Reset Otomatis (Rules group_tarif)
              </Button>
            </div>

            {/* Right Status / Save Batch */}
            <div className="d-flex align-items-center gap-2 ms-auto">
              {modifiedCount > 0 && (
                <Button
                  variant="danger"
                  size="sm"
                  className="fw-bold shadow-sm d-flex align-items-center gap-1"
                  style={{ fontSize: '0.75rem', padding: '0.35rem 0.85rem' }}
                  onClick={handleSaveAllModified}
                  disabled={isBulkSaving}
                >
                  {isBulkSaving ? (
                    <><Spinner size="sm" animation="border" className="me-1" /> Menyimpan...</>
                  ) : (
                    <><i className="bi bi-check-all fs-6"></i> Simpan Semua Perubahan ({modifiedCount})</>
                  )}
                </Button>
              )}

              <Button
                variant="outline-secondary"
                size="sm"
                className="shadow-sm"
                style={{ fontSize: '0.75rem', padding: '0.35rem 0.6rem' }}
                onClick={() => fetchList(currentPage, selectedGroupFilter, searchQuery)}
                disabled={isLoading}
                title="Segarkan data tabel"
              >
                <i className={`bi bi-arrow-clockwise ${isLoading ? 'spinner-border spinner-border-sm' : ''}`}></i>
              </Button>
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="p-3 bg-light bg-opacity-50 border-bottom">
            <Row className="g-2 align-items-center">
              <Col md={5} sm={12}>
                <InputGroup size="sm">
                  <InputGroup.Text className="bg-white border-end-0 text-muted">
                    <i className="bi bi-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Cari Kode Layanan atau Nama Tindakan..."
                    value={searchQuery}
                    onChange={e => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    style={{ fontSize: '0.78rem' }}
                  />
                  {searchQuery && (
                    <Button variant="outline-secondary" size="sm" onClick={() => { setSearchQuery(''); setCurrentPage(1); }}>
                      ✕
                    </Button>
                  )}
                </InputGroup>
              </Col>

              <Col md={4} sm={6}>
                <Form.Select
                  size="sm"
                  value={selectedGroupFilter}
                  onChange={e => {
                    setSelectedGroupFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ fontSize: '0.78rem', fontWeight: 500 }}
                >
                  <option value="ALL">Semua Billing Group ({totalCount.toLocaleString('id-ID')} Total)</option>
                  {BILLING_GROUPS.map(g => (
                    <option key={g} value={g}>
                      {g} {groupCounts[g] !== undefined ? `(${groupCounts[g]})` : ''}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={3} sm={6} className="d-flex align-items-center justify-content-end gap-2">
                <span className="text-muted" style={{ fontSize: '0.75rem' }}>Tampilkan:</span>
                <Form.Select
                  size="sm"
                  value={itemsPerPage}
                  onChange={e => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{ width: '100px', fontSize: '0.78rem' }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </Form.Select>
              </Col>
            </Row>
          </div>

          {/* Table Body */}
          <div className="table-responsive" style={{ minHeight: '380px' }}>
            <Table hover bordered size="sm" className="align-middle mb-0" style={{ fontSize: '0.76rem' }}>
              <thead className="table-light text-secondary text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.3px' }}>
                <tr>
                  <th style={{ width: '45px', textAlign: 'center' }}>No</th>
                  <th style={{ width: '110px' }}>Kode Layanan</th>
                  <th>Nama Layanan (Tindakan)</th>
                  <th style={{ width: '250px' }}>Billing Group E-Klaim (18 Opsi)</th>
                  <th style={{ width: '170px' }}>Kode KPTL (Opsional)</th>
                  <th style={{ width: '110px', textAlign: 'center' }}>Sumber / Status</th>
                  <th style={{ width: '90px', textAlign: 'center' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5 text-muted">
                      <Spinner animation="border" variant="teal" style={{ color: '#0d9488' }} size="sm" className="me-2" />
                      Memuat data mapping billing group...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5 text-muted">
                      <i className="bi bi-inbox fs-2 d-block text-secondary mb-1"></i>
                      Tidak ada data tindakan ditemukan.
                    </td>
                  </tr>
                ) : (
                  items.map((item, idx) => {
                    const rowNumber = (currentPage - 1) * itemsPerPage + idx + 1;
                    const isModified = !!modifiedRows[item.kd_tindakan];
                    const isSavingThis = savingKey === item.kd_tindakan;
                    const color = GROUP_COLORS[item.billing_group] || { bg: '#f1f5f9', text: '#334155', border: '#cbd5e1' };

                    return (
                      <tr key={item.kd_tindakan} style={{ background: isModified ? '#fffbeb' : undefined }}>
                        <td className="text-center text-muted fw-semibold">{rowNumber}</td>
                        <td className="fw-bold font-monospace" style={{ color: '#0f766e' }}>
                          {item.kd_tindakan}
                        </td>
                        <td className="fw-semibold text-dark">
                          {item.nm_tindakan}
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-1">
                            <Form.Select
                              size="sm"
                              value={item.billing_group || ''}
                              onChange={e => handleRowChange(item.kd_tindakan, 'billing_group', e.target.value)}
                              style={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                background: color.bg,
                                color: color.text,
                                borderColor: color.border
                              }}
                            >
                              <option value="">-- Pilih Group --</option>
                              {BILLING_GROUPS.map(g => (
                                <option key={g} value={g}>{g}</option>
                              ))}
                            </Form.Select>
                          </div>
                        </td>
                        <td>
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder="Contoh: KPTL-001"
                            value={item.kode_kptl || ''}
                            onChange={e => handleRowChange(item.kd_tindakan, 'kode_kptl', e.target.value)}
                            style={{ fontSize: '0.74rem' }}
                          />
                        </td>
                        <td className="text-center">
                          {item.source === 'IMPORT' ? (
                            <Badge bg="info" className="text-dark" style={{ fontSize: '0.67rem' }}>
                              <i className="bi bi-file-earmark-arrow-up me-1"></i>IMPORT
                            </Badge>
                          ) : item.source === 'MANUAL' ? (
                            <Badge bg="primary" style={{ fontSize: '0.67rem' }}>
                              <i className="bi bi-person-fill me-1"></i>MANUAL
                            </Badge>
                          ) : (
                            <Badge bg="secondary" style={{ fontSize: '0.67rem' }}>
                              <i className="bi bi-cpu-fill me-1"></i>AUTO
                            </Badge>
                          )}
                          {isModified && (
                            <Badge bg="warning" text="dark" className="ms-1" style={{ fontSize: '0.65rem' }}>
                              Diubah
                            </Badge>
                          )}
                        </td>
                        <td className="text-center">
                          <Button
                            variant={isModified ? 'warning' : 'outline-primary'}
                            size="sm"
                            className="py-0 px-2 fw-semibold"
                            style={{ fontSize: '0.7rem' }}
                            disabled={isSavingThis}
                            onClick={() => handleSaveSingle(item)}
                            title="Simpan baris ini ke database"
                          >
                            {isSavingThis ? (
                              <Spinner size="sm" animation="border" />
                            ) : (
                              <><i className="bi bi-save me-1"></i>Simpan</>
                            )}
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          {/* Card Footer Pagination */}
          <div className="p-3 border-top d-flex flex-wrap align-items-center justify-content-between gap-2 bg-white" style={{ borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px' }}>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
              Menampilkan {items.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} s/d {Math.min(currentPage * itemsPerPage, totalCount)} dari <strong>{totalCount.toLocaleString('id-ID')}</strong> layanan tindakan
            </div>

            {totalPages > 1 && (
              <Pagination size="sm" className="mb-0">
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
                
                {currentPage > 3 && <Pagination.Ellipsis disabled />}
                
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p >= currentPage - 2 && p <= currentPage + 2)
                  .map(p => (
                    <Pagination.Item
                      key={p}
                      active={p === currentPage}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </Pagination.Item>
                  ))}

                {currentPage < totalPages - 2 && <Pagination.Ellipsis disabled />}

                <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            )}
          </div>
        </Card>
      </div>

      {/* ══ MODAL UPLOAD EXCEL / CSV ══ */}
      <Modal show={showUploadModal} onHide={() => !isUploading && setShowUploadModal(false)} centered size="lg">
        <Modal.Header closeButton={!isUploading} style={{ background: '#0d9488', color: 'white' }}>
          <Modal.Title className="fs-6 fw-bold d-flex align-items-center gap-2">
            <i className="bi bi-cloud-arrow-up-fill text-warning fs-5"></i>
            Upload File Template Billing Group (.xlsx / .csv)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Alert variant="info" className="py-2 px-3 mb-3" style={{ fontSize: '0.78rem' }}>
            <div className="fw-bold mb-1"><i className="bi bi-info-circle-fill me-1"></i> Standar File Template:</div>
            <div>File harus memiliki format baku template resmi:</div>
            <ul className="mb-0 ps-3">
              <li><strong>Baris 1:</strong> Nomor kolom (<code>1, 2, 3, 4</code>)</li>
              <li><strong>Baris 2:</strong> Header kolom (<code>kode layanan, nama layanan, billing group e-klaim, kode kptl (opsional)</code>)</li>
              <li><strong>Baris 3+:</strong> Data tindakan dengan 18 nama Billing Group baku E-Klaim</li>
            </ul>
          </Alert>

          {uploadResult && (
            <Alert variant={uploadResult.type} className="py-2 px-3 mb-3" style={{ fontSize: '0.78rem' }}>
              <strong>{uploadResult.message}</strong>
              {uploadResult.skipped && uploadResult.skipped.length > 0 && (
                <div className="mt-2 text-danger">
                  <div>Baris dilewati / tidak valid ({uploadResult.skipped.length}):</div>
                  <ul className="mb-0 ps-3">
                    {uploadResult.skipped.slice(0, 5).map((s, idx) => (
                      <li key={idx}>Kode: {s.kd_tindakan} ({s.reason})</li>
                    ))}
                    {uploadResult.skipped.length > 5 && <li>...dan {uploadResult.skipped.length - 5} lainnya</li>}
                  </ul>
                </div>
              )}
            </Alert>
          )}

          <Form onSubmit={handleUploadSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold" style={{ fontSize: '0.8rem' }}>
                Pilih Dokumen Template Excel (.xlsx) atau CSV (.csv):
              </Form.Label>
              <Form.Control
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={e => setUploadFile(e.target.files[0] || null)}
                disabled={isUploading}
                style={{ fontSize: '0.8rem' }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.72rem' }}>
                Anda dapat mendownload template saat ini terlebih dahulu melalui tombol <strong>Download Template Excel</strong>, melakukan penyesuaian di Excel, lalu mengunggahnya kembali ke sini.
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowUploadModal(false)}
                disabled={isUploading}
              >
                Tutup
              </Button>
              <Button
                variant="success"
                size="sm"
                type="submit"
                disabled={!uploadFile || isUploading}
                className="fw-bold d-flex align-items-center gap-1"
                style={{ background: '#0d9488', borderColor: '#0d9488' }}
              >
                {isUploading ? (
                  <><Spinner size="sm" animation="border" className="me-1" /> Mengimpor Data...</>
                ) : (
                  <><i className="bi bi-upload"></i> Proses & Terapkan File</>
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL RESET AUTO CONFIRM ══ */}
      <Modal show={showResetConfirmModal} onHide={() => !isResetting && setShowResetConfirmModal(false)} centered>
        <Modal.Header closeButton={!isResetting} className="bg-warning text-dark">
          <Modal.Title className="fs-6 fw-bold d-flex align-items-center gap-2">
            <i className="bi bi-exclamation-triangle-fill fs-5"></i>
            Konfirmasi Sinkronisasi Otomatis
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3" style={{ fontSize: '0.82rem' }}>
          <p className="mb-2">
            Apakah Anda yakin ingin menerapkan ulang algoritma klasifikasi otomatis <code>group_tarif()</code> untuk seluruh <strong>{totalCount}</strong> layanan tindakan?
          </p>
          <div className="p-2 bg-light rounded text-muted" style={{ fontSize: '0.75rem' }}>
            <i className="bi bi-info-circle me-1 text-primary"></i>
            Tindakan yang sebelumnya diatur manual atau diimpor akan disesuaikan kembali mengikuti kata kunci nama tindakan, jenis penunjang, dan kategori master.
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowResetConfirmModal(false)}
            disabled={isResetting}
          >
            Batal
          </Button>
          <Button
            variant="warning"
            size="sm"
            className="fw-bold text-dark"
            onClick={handleResetAuto}
            disabled={isResetting}
          >
            {isResetting ? (
              <><Spinner size="sm" animation="border" className="me-1" /> Mereset...</>
            ) : (
              <><i className="bi bi-magic me-1"></i> Ya, Terapkan Klasifikasi Otomatis</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
}
